import debounce from "lodash/debounce";
import { apiGetSignalsData } from "@/Groups/Signals/private/api/signalsData";
import { getLastSignals } from "@/Groups/Signals/private/api/lastSignals";
import { apiPinSignal } from "@/Groups/Signals/private/api/pinSignal";
import { apiGetTheSignal } from "@/Groups/Signals/private/api/apiGetTheSignal";
import { toggleFavouritesSignal } from "@/Groups/Signals/private/api/toggleFavouritesSignal";
import { apiSendSignalToServer, apiSendChangedSignalToServer } from "@/Groups/Signals/private/api/sendSignalToServer";

const Signals = {
  namespaced: true,

  state: { 
    data: {},
    map: [],
    next: null,
    isLoading: false,
    isReady: false,
    isLoadingNext: false,

    //Для синхронизации избранного с сервером
    optimisticOriginal: {},
    debounceTimers: {},

    //Последние 6 сигналов
    lastSignals: [],
    isLoadingLastSignals: false,
    readyLastSignals: false,

    //Черновик для сохранения формы
    draftForm: {},
    //ID редактируемого сигнала
    idEditSignal: null,
    //Идет ли процесс отправки сигнала на сервер
    isSignalSendLoading: false,
  },

  actions: {
    //Метод инициализации сигналов
    async initSignals({ state, commit, dispatch }){
      await dispatch('getSignals');
      if (Object.keys(state.data).length === 0) return;
      commit('SET_READY_FLAG');
    },

    //Получаем список сигналов
    async getSignals({ commit }){
      commit('TOGGLE_LOADING_FLAG', true);
      try{
        const data = await apiGetSignalsData();
        commit('CLEAR_DATA');
        commit('SET_DATA', data.results);
        commit('SET_NEW_LINK', data.next);
        commit('SORT_SIGNALS');
      } catch (error) {
        console.error('Ошибка при добавлении нового сигнала:', error);
      } finally{
        commit('TOGGLE_LOADING_FLAG', false);
      }
    },

    //Получить и добавить сигнал
    async getAndAddTheSignal({ commit }, id){
      try {
        const data = await apiGetTheSignal(id);
        commit('SET_NEW_SIGNAL', data);
        return data;
      } catch (error) {
        console.error('Ошибка при получении конкретного сигнала:', error);
        return Promise.reject(error);
      }
    },

    //Подгружаем следующие сигналы
    async loadNextSignals({ commit, state }) {
      commit('TOGGLE_LOADING_NEW_PAGE_FLAG', true);
      try{
        const data = await apiGetSignalsData(state.next);
        commit('SET_DATA', data.results);
        commit('SET_NEW_LINK', data.next);
      } catch (error) {
        console.error('Ошибка при получении следующих сигналлв:', error);
      } finally{
        commit('TOGGLE_LOADING_NEW_PAGE_FLAG', false);
      }
    },

    //Переключение сигнала в избранное
    toggleFavouriteOptimistic({ state, commit }, id) {
      const signal = state.data[id];
      if (!signal) return;
      const currentValue = signal.reactions?.bookmarked?.is_mine ?? false;
      if (!(id in state.optimisticOriginal)) {
        state.optimisticOriginal[id] = currentValue;
      }
      const newValue = !currentValue;
      commit("SET_BOOKMARK_STATE", { id, value: newValue });
      if (!state.debounceTimers[id]) {
        state.debounceTimers[id] = debounce(async () => {
          try {
            await toggleFavouritesSignal({ object_id: id, content_type: signal.content_type });
            delete state.optimisticOriginal[id];
          } catch (error) {
            console.error("Ошибка сервера, откат:", error);
            commit("SET_BOOKMARK_STATE", { id, value: state.optimisticOriginal[id] });
            delete state.optimisticOriginal[id];
          } finally {
            delete state.debounceTimers[id];
          }
        }, 1000);
      }
      state.debounceTimers[id]();
    },

    //Получить последние сигналы
    async getLastSignals({ commit }) {
      commit('SET_LOADING_LAST_SIGNALS', true);
      try {
        const data = await getLastSignals();
        commit('SET_LAST_SIGNALS', data);
        commit('SET_READY_LAST_SIGNALS', true);
        return data;
      } catch (error) {
        console.error('Ошибка при получении последних сигналов:', error);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING_LAST_SIGNALS', false);
      }
    },

    //Закрепить/открепить сигнал
    async pinSignal({ commit, state }, { id }){
      try {
        const methodType = state.data[id].pinned ? 'DELETE' : 'POST';
        const data = await apiPinSignal({ id, methodType });
        commit('PIN_SIGNAL', id);
        commit('SORT_SIGNALS');
        return data;
      } catch (error) {
        console.error('Ошибка при откреплении / прикриплении сигнала:', error);
        return Promise.reject(error);
      }
    },

    //Добавить сигнал
    async addSignal({ commit }, formData){
      commit('TOGGLE_SIGNAL_SEND_LOADING_FLAG', true);
      try {
        const data = await apiSendSignalToServer(formData);
        commit('SET_NEW_SIGNAL', data);
        commit('SORT_SIGNALS');
        commit('UPDATE_DRAFT_FORM', null);
        return data;
      } catch (error) {
        console.error('Ошибка при добавлении сигнала:', error);
        return Promise.reject(error);
      } finally {
        commit('TOGGLE_SIGNAL_SEND_LOADING_FLAG', false);
      }
    },

    //Изменить сигнал
    async changeSignal({ commit, state }, formData){
      commit('TOGGLE_SIGNAL_SEND_LOADING_FLAG', true);
      try {
        const data = await apiSendChangedSignalToServer(formData, state.idEditSignal);
        commit('UPDATE_SIGNAL', data);
        return data;
      } catch (error) {
        console.error('Ошибка при добавлении сигнала:', error);
        return Promise.reject(error);
      } finally {
        commit('TOGGLE_SIGNAL_SEND_LOADING_FLAG', false);
      }
    },

    //Добавить готовый объект сигнала в список
    async getAndAddTheSignal({ commit }, id){
      try {
        const data = await apiGetTheSignal(id);
        commit('SET_NEW_SIGNAL', data);
        return data;
      } catch (error) {
        console.error('Ошибка при получении конкретного сигнала:', error);
        return Promise.reject(error);
      } finally {

      }
    },

    //Изменить значение счетчика комментариев
    async updateCommentsCounter({ commit }, { id, count, isAlignValues }){
      if(isAlignValues){
        commit('ALIGN_COMMENTS', id);
      } else if(count){
        commit('UPDATE_COMMENTS_COUNTER', { id, count })
      }
    },

    //Обновить трендовые сигналы
    async updateTrendingSignals({ commit, state }, data){ 
      data.forEach(item => {
        if(state.map.includes(item.id)){
          commit('MAKE_SIGNAL_TRENDING', { id: item.id, status: true });
        }else{
          commit('SET_NEW_SIGNAL', item);
        }
      });
      commit('SORT_SIGNALS');
    },

    //Обновить список данных сигналов
    async updateData({ commit }, data){ commit('UPDATE_DATA', data); },

    //Сохранить черновик формы
    async saveDraftForm({ commit }, data){ commit('UPDATE_DRAFT_FORM', data) },

    //Изменить статус live сигнала
    async approveLiveStatus({ commit }, id){ commit('CHANGE_LIVE_STATUS', {id: id, status: true}); },

    //Изменить id редактируемого сигнала
    async updateIdEditSignal({ commit }, id){ commit('CHANGE_ID_EDIT_SIGNAL', id) },

    //Обновить даннные на актуальные
    async refreshData({ commit }, data){ commit('REFRESH_DATA', data ); },
  },

  mutations: {
    //Добавлям данные в хранилище
    SET_DATA(state, { favorites, list }){
      favorites.forEach(signal => {
        if(!state.map.includes(signal.id)){
          state.data[signal.id] = signal;
          state.map.push(signal.id);
        }
      });
      list.forEach(signal => {
        if(!state.map.includes(signal.id)){
          state.data[signal.id] = signal;
          state.map.push(signal.id);
        }
      });
    },

    //Сортировка сигналов
    SORT_SIGNALS(state) {
      state.map.sort((a, b) => {
        const signalA = state.data[a];
        const signalB = state.data[b];
        const priorityA = Number(signalA.pinned) * 2 + Number(signalA.trending);
        const priorityB = Number(signalB.pinned) * 2 + Number(signalB.trending);
        if (priorityA !== priorityB) return priorityB - priorityA;
        const dateA = new Date(signalA.publication_date || signalA.created_at);
        const dateB = new Date(signalB.publication_date || signalB.created_at);
        return dateB - dateA;
      });
    },

    //Поменять флаг избранного у сигнала
    SET_BOOKMARK_STATE(state, { id, value }) {
      if (!state.data[id]) return;
      if (!state.data[id].reactions) state.data[id].reactions = {};
      if (!state.data[id].reactions.bookmarked)
        state.data[id].reactions.bookmarked = {};

      state.data[id].reactions.bookmarked.is_mine = value;
    },

    //Очистить данные
    CLEAR_DATA(state){
      state.data = {};
      state.map = [];
      state.next = null;
    },

    //Добавить в список новый сигнал
    SET_NEW_SIGNAL(state, signal){
      if(!state.map.includes(signal.id)){
        state.data[signal.id] = signal;
        state.map.unshift(signal.id);
      }
    },

    //Обновляю информацию о реакциях к сигналам
    CHANGE_REACTIONS(state, {object_id, reactions}){
      if (!state.data[object_id]) return;
      state.data[object_id].reactions.is_mine = reactions.is_mine;
      state.data[object_id].reactions.reactions_values = reactions.reactions_values;
    },

    //Обнвоить данные сигналов
    UPDATE_DATA(state, data){
      data.forEach(item => {
        if(!(item.id in state.data)){
          state.data[item.id] = item;
          state.map.unshift(item.id);
        } 
      })
    },

    //Обновить сигнал
    UPDATE_SIGNAL(state, updData){
      state.data[updData.id].title = updData.title;
      state.data[updData.id].content = updData.content;
      state.data[updData.id].link = updData.link
      state.data[updData.id].coin = updData.coin;
      state.data[updData.id].images_list = updData.images_list;
    },

    //Обновить данные на актуальные
    REFRESH_DATA(state, data){
      data.forEach(item => {
        const id = item.id;
        state.data[id].change_1d_usd = item.change_1d_usd;
        state.data[id].change_all_time_percent = item.change_all_time_percent;
        state.data[id].price_usd = item.price_usd;
        state.data[id].start_market_cap = item.start_market_cap;
        state.data[id].start_price_usd = item.start_price_usd;
        state.data[id].coin = item.coin,
        state.data[id].ath = item.ath;
        state.data[id].change_ath = item.change_ath;
        state.data[id].atl = item.atl;
        state.data[id].change_atl = item.change_atl;
      })
    },

    //Отмечаем, что данные сигналов готовы к использованию
    SET_READY_FLAG(state){ state.isReady = true; },
    
    //Отмечаем, что начался/закончился процесс получения данных с сервера
    TOGGLE_LOADING_FLAG(state, status){ state.isLoading = status; },

    //Указать ссылку на следующую страницу
    SET_NEW_LINK(state, link){ state.next = link; },

    //Отмечаем, что начался/закончился процесс получения новых данных с сервера
    TOGGLE_LOADING_NEW_PAGE_FLAG(state, status){ state.isLoadingNext = status; },

    //Положить последние сигналы
    SET_LAST_SIGNALS(state, data){ state.lastSignals = data; },

    //Установить флаг загрузки последних сигналов
    SET_LOADING_LAST_SIGNALS(state, status) { state.isLoadingLastSignals = status; },

    //Установить флаг готовности последних сигналов
    SET_READY_LAST_SIGNALS(state, status) { state.readyLastSignals = status; },

    //Прикрепить / открепить сигнал
    PIN_SIGNAL(state, id){ state.data[id].pinned = !state.data[id].pinned; },

    //Изменить ID редактируемого сигнала
    CHANGE_ID_EDIT_SIGNAL(state, id){ state.idEditSignal = id ? id : null },

    //Отмечаем, что начался/закончился процесс отправки данных сигнала на сервер
    TOGGLE_SIGNAL_SEND_LOADING_FLAG(state, status){ state.isSignalSendLoading = status; },
    
    //Сохраняем черновик формы
    UPDATE_DRAFT_FORM(state, data){ state.draftForm = data; },

    //Изменит статус LIVE сигнала
    CHANGE_LIVE_STATUS(state, {id, status}){ state.data[id].live = status; },

    MAKE_SIGNAL_TRENDING(state, { id, status }){ state.data[id].trending = status },

    //Выровнять кол-во комментариев с начальным количеством
    ALIGN_COMMENTS(state, id){ state.data[id].init_comments_count = state.data[id].comments_count; },

    //Обновляем счетчик комментариев
    UPDATE_COMMENTS_COUNTER(state, { id, count }){
      if(!state.data[id]) return;
      state.data[id].comments_count = count;
      if(state.data[id].comments_count < state.data[id].init_comments_count){
        state.data[id].init_comments_count = state.data[id].comments_count;
      }
    },
  },

  getters: {
    //Получить массив ID сигналов в отсортированном порядке
    map: (state) => state.map,
    
    //Получить объект со всеми сигналами
    data: (state) => state.data,
    
    //Получить ссылку на следующую страницу
    next: (state) => state.next,
    
    //Получить массив избранных сигналов (только те, у которых is_mine в bookmarked = true)
    favoritesMap: (state) => {
      return state.map.filter(id => state.data[id]?.reactions?.bookmarked?.is_mine === true);
    },

    //Получить массив обычных сигналов (все, которые НЕ находятся в избранном)
    usualSignalsMap: (state) => {
      return state.map.filter(id => state.data[id]?.reactions?.bookmarked?.is_mine !== true);
    },

    //Флаг: идет ли сейчас первичная загрузка всех сигналов
    isLoading: (state) => state.isLoading,

    //Флаг: готовы ли данные к использованию после инициализации
    isReady: (state) => state.isReady,

    //Флаг: идет ли загрузка следующей страницы сигналов (пагинация)
    isLoadingNext: (state) => state.isLoadingNext,
  }
}

export default Signals;